import React, { useContext, useEffect, useState, useCallback } from 'react';
import { MdTrendingUp, MdWarningAmber, MdPieChart, MdSchool, MdCalculate, MdShield, MdHome, MdArrowBack } from 'react-icons/md';
import { AppContext } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import {
  fetchInvestingAnalysis, fetchRiskQuestions, saveRiskProfile, getRiskProfile, planGoal,
} from '../services/backendAPI';

// Investing advisor — Step A: EDUCATION + PLANNING + holdings analysis only.
// There are deliberately no individualized buy/sell recommendations and no trade
// setup or execution here. Everything is framed as education, not regulated
// advice. Holdings analysis, the risk questionnaire, and the goal planner all
// run against the server-side investing engine (lib/investing).

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const pct = (x) => `${Number(x || 0).toFixed(1)}%`;

const CLASS_LABEL = {
  equity: 'Stocks', etf: 'ETFs', fund: 'Funds', fixed_income: 'Bonds / Fixed income',
  cash: 'Cash', crypto: 'Crypto', other: 'Other',
};
const CLASS_COLOR = {
  equity: '#1976d2', etf: '#00897b', fund: '#6a1b9a', fixed_income: '#d9822b',
  cash: '#78909c', crypto: '#f4511e', other: '#9e9e9e',
};
const BUCKET_LABEL = {
  conservative: 'Conservative', balanced: 'Balanced', growth: 'Growth', aggressive: 'Aggressive',
};

// ── Holdings analysis card ──────────────────────────────────────────────────────
function HoldingsCard({ analysis }) {
  const mix = Object.entries(analysis.assetClass || {}).sort((a, b) => b[1].value - a[1].value);
  return (
    <div className="ins-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <MdPieChart size={20} color="#1976d2" />
        <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: 15 }}>Your portfolio</div>
        <div style={{ marginLeft: 'auto', fontWeight: 800, color: 'var(--text-dark)' }}>{money(analysis.totalValue)}</div>
      </div>

      {/* Asset mix as a stacked bar + legend */}
      {analysis.totalValue > 0 && (
        <>
          <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
            {mix.map(([type, v]) => (
              <div key={type} title={`${CLASS_LABEL[type] || type} ${pct(v.pct)}`}
                style={{ width: `${v.pct}%`, background: CLASS_COLOR[type] || '#9e9e9e' }} />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginBottom: 10 }}>
            {mix.map(([type, v]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-medium)' }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: CLASS_COLOR[type] || '#9e9e9e' }} />
                {CLASS_LABEL[type] || type} · {pct(v.pct)}
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--text-medium)' }}>
        {analysis.topConcentration && (
          <Stat label="Largest holding" value={`${analysis.topConcentration.name} · ${pct(analysis.concentrationPct)}`} />
        )}
        <Stat label="Registered" value={pct(analysis.registered?.registeredPct)} />
        <Stat label="Non-registered" value={pct(analysis.registered?.nonRegisteredPct)} />
        {analysis.weightedAvgMer != null && (
          <Stat label="Avg fee (MER)" value={pct(analysis.weightedAvgMer * 100)} />
        )}
      </div>

      {(analysis.flags || []).length > 0 && (
        <div style={{ marginTop: 12 }}>
          {analysis.flags.map((f) => (
            <div key={f.code} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
              <MdWarningAmber size={16} color="#d9822b" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: 'var(--text-medium)' }}>{f.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FHSA contribution-room card ─────────────────────────────────────────────────
function FhsaCard({ fhsa, onChatHandoff }) {
  return (
    <div className="ins-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <MdHome size={20} color="#f4845f" />
        <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: 15 }}>First Home Savings (FHSA)</div>
        {fhsa.balance != null && (
          <div style={{ marginLeft: 'auto', fontWeight: 800, color: 'var(--text-dark)' }}>{money(fhsa.balance)}</div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--text-medium)' }}>
        {fhsa.balance != null && <Stat label="Balance" value={money(fhsa.balance)} />}
        {fhsa.contribution_room_remaining != null && (
          <Stat label="Room this year" value={money(fhsa.contribution_room_remaining)} />
        )}
        {fhsa.lifetime_room_remaining != null && (
          <Stat label="Lifetime room left" value={money(fhsa.lifetime_room_remaining)} />
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 10 }}>
        Contributions are tax-deductible; qualifying first-home withdrawals are tax-free. Up to $8,000/year, $40,000 lifetime.
      </div>

      <div style={{ marginTop: 12 }}>
        <button
          onClick={() => onChatHandoff?.('I want to make an FHSA contribution — what do I need to know?')}
          style={{ ...pillStyle, background: '#f4845f' }}>
          Contribute now
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: 'var(--card-subtle, rgba(0,0,0,0.04))', borderRadius: 8, padding: '6px 10px' }}>
      <div style={{ fontSize: 10, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontWeight: 700, color: 'var(--text-dark)' }}>{value}</div>
    </div>
  );
}

// ── Risk profile + questionnaire ────────────────────────────────────────────────
function RiskCard({ customerId, riskProfile, onSaved }) {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const startQuiz = useCallback(async () => {
    const { questions: qs } = await fetchRiskQuestions();
    setQuestions(qs || []);
    setAnswers({});
    setOpen(true);
  }, []);

  const submit = useCallback(async () => {
    setSaving(true);
    const res = await saveRiskProfile(customerId, answers);
    setSaving(false);
    if (res) { setOpen(false); onSaved?.(res.riskProfile || res); }
  }, [customerId, answers, onSaved]);

  const alloc = riskProfile?.allocation;
  const allDone = questions.length > 0 && questions.every((q) => answers[q.id] != null);

  return (
    <div className="ins-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <MdShield size={20} color="#008a0d" />
        <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: 15 }}>Your risk profile</div>
      </div>

      {!open && riskProfile?.bucket && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-dark)' }}>
            {BUCKET_LABEL[riskProfile.bucket] || riskProfile.bucket}
          </div>
          {alloc && (
            <div style={{ fontSize: 12, color: 'var(--text-medium)', marginTop: 4 }}>
              Target mix — Stocks {alloc.equity}% · Bonds {alloc.fixed_income}% · Cash {alloc.cash}%
            </div>
          )}
        </div>
      )}

      {!open && !riskProfile?.bucket && (
        <div style={{ fontSize: 13, color: 'var(--text-medium)', marginBottom: 10 }}>
          Answer a few questions to find a risk level that fits you. This is educational and
          takes about a minute.
        </div>
      )}

      {open && (
        <div style={{ marginBottom: 10 }}>
          {questions.map((q) => (
            <div key={q.id} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 6 }}>{q.prompt}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {q.options.map((opt, i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-medium)', cursor: 'pointer' }}>
                    <input type="radio" name={q.id} checked={answers[q.id] === i}
                      onChange={() => setAnswers((a) => ({ ...a, [q.id]: i }))} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        {!open && (
          <button className="pill-btn" onClick={startQuiz} style={pillStyle}>
            {riskProfile?.bucket ? 'Retake questionnaire' : 'Start questionnaire'}
          </button>
        )}
        {open && (
          <>
            <button onClick={submit} disabled={!allDone || saving} style={{ ...pillStyle, opacity: allDone && !saving ? 1 : 0.5 }}>
              {saving ? 'Saving…' : 'See my risk level'}
            </button>
            <button onClick={() => setOpen(false)} style={{ ...pillStyle, background: 'transparent', color: 'var(--text-medium)', border: '1px solid var(--border-color, #ccc)' }}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Goal planner ────────────────────────────────────────────────────────────────
function PlannerCard() {
  const [target, setTarget] = useState('25000');
  const [current, setCurrent] = useState('5000');
  const [years, setYears] = useState('10');
  const [rate, setRate] = useState('5');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    setBusy(true);
    const res = await planGoal(
      { target_amount: Number(target), current_amount: Number(current), years: Number(years) },
      { annualReturn: Number(rate) / 100 },
    );
    setBusy(false);
    setResult(res);
  }, [target, current, years, rate]);

  return (
    <div className="ins-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <MdCalculate size={20} color="#6a1b9a" />
        <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: 15 }}>Goal planner</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <Field label="Goal amount ($)" value={target} onChange={setTarget} />
        <Field label="Saved so far ($)" value={current} onChange={setCurrent} />
        <Field label="Years" value={years} onChange={setYears} />
        <Field label="Assumed return (%)" value={rate} onChange={setRate} />
      </div>
      <button onClick={run} disabled={busy} style={pillStyle}>{busy ? 'Calculating…' : 'Calculate'}</button>
      {result && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-dark)' }}>
          To reach <b>{money(result.target)}</b> in <b>{result.years}</b> years, contribute about{' '}
          <b>{money(result.requiredMonthly)}/month</b>. Projected balance: {money(result.projected)}.
          <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 6 }}>
            Educational estimate — assumes a steady {Number(rate)}% annual return, which is not guaranteed.
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <label style={{ fontSize: 11, color: 'var(--text-light)' }}>
      {label}
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', marginTop: 3, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border-color, #ccc)',
          background: 'var(--input-bg, #fff)', color: 'var(--text-dark)', boxSizing: 'border-box' }} />
    </label>
  );
}

// ── Education entry points ──────────────────────────────────────────────────────
const EDU_TOPICS = [
  { topic: 'tfsa_vs_rrsp', title: 'TFSA vs RRSP' },
  { topic: 'diversification', title: 'Diversification' },
  { topic: 'compound_growth', title: 'Compound growth' },
  { topic: 'mer_fees', title: 'MER & fees' },
  { topic: 'dollar_cost_averaging', title: 'Dollar-cost averaging' },
];

function EducationCard({ onChatHandoff }) {
  return (
    <div className="ins-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <MdSchool size={20} color="#00897b" />
        <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: 15 }}>Learn the basics</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {EDU_TOPICS.map((t) => (
          <button key={t.topic}
            onClick={() => onChatHandoff?.(`Explain ${t.title} in plain language.`)}
            style={{ ...pillStyle, background: 'transparent', color: '#00897b', border: '1px solid #00897b' }}>
            {t.title}
          </button>
        ))}
      </div>
    </div>
  );
}

const pillStyle = {
  padding: '8px 14px', borderRadius: 20, border: 'none', background: '#1976d2', color: '#fff',
  fontWeight: 700, fontSize: 13, cursor: 'pointer',
};

// ── Screen ──────────────────────────────────────────────────────────────────────
const InvestingScreen = ({ onChatHandoff, onNavigate, onBack }) => {
  const { sessionId, sessionSummary } = useContext(AppContext);
  const { loadedProfileData } = useProfile();
  const customerId = sessionSummary?.customerId || null;

  const [analysis, setAnalysis] = useState(null);
  const [riskProfile, setRiskProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    setAnalysis(null);
    (async () => {
      const [a, rp] = await Promise.all([
        fetchInvestingAnalysis(sessionId),
        getRiskProfile(customerId),
      ]);
      if (cancelled) return;
      setAnalysis(a);
      setRiskProfile(a?.riskProfile || rp);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sessionId, customerId]);

  const profileName = loadedProfileData?.name;
  const hasHoldings = analysis && analysis.totalValue > 0;

  return (
    <div className="screen insights-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('services'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h1 className="bh-flow-title">Investing</h1>
            {profileName && (
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', fontWeight: 500, marginTop: 2 }}>{profileName}</div>
            )}
          </div>
          <MdTrendingUp size={26} style={{ marginLeft: 'auto' }} />
        </div>
      </header>

      <div className="scroll-content gray-bg ins-scroll">
        <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', marginBottom: 2 }}>
          education & planning — not individualized investment advice
        </div>

        {!sessionId && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            Open a profile to explore investing.
          </div>
        )}

        {sessionId && loading && !analysis && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            IVA is reviewing your investments…
          </div>
        )}

        {sessionId && hasHoldings && <HoldingsCard analysis={analysis} />}

        {sessionId && analysis?.fhsa && <FhsaCard fhsa={analysis.fhsa} onChatHandoff={onChatHandoff} />}

        {sessionId && analysis && !hasHoldings && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            No investment holdings found for this profile — you can still set a risk profile and plan a goal below.
          </div>
        )}

        {sessionId && (
          <RiskCard customerId={customerId} riskProfile={riskProfile} onSaved={setRiskProfile} />
        )}

        {sessionId && <PlannerCard />}

        <EducationCard onChatHandoff={onChatHandoff} />

        <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', margin: '8px 12px 0' }}>
          For personalized recommendations or to place a trade, speak with a TD advisor.
        </div>
      </div>
    </div>
  );
};

export default InvestingScreen;
